import { commonShortFieldLabels, commonShortNouns } from "./common-short-fields";
import { platformAdminExecutionControlPlane } from "./platform-admin-execution-control-plane";
import { platformAdminExecutionEvents } from "./platform-admin-execution-events";
import { platformAdminExecutionRuntimeValues } from "./platform-admin-execution-runtime-values";

export const platformAdminExecution = {
  title: "実行基盤",
  subtitle: "実行プラットフォームの読み取り専用ランタイムビューです。",
  directRead: "このページは execution-fabric の主実行データとタスク API を直接参照しています。",
  indirectRead:
    "このページは execution-fabric 直結モードに収束しています。実行制御面のデータを見るには統合認証でログインしてください。",
  summaryProvider: "実行提供元：{value}",
  summaryImages: "イメージ：{count}",
  summaryInstances: "実行インスタンス：{count}",
  summaryDocker: "デバッグコンテナ：{count}",
  summaryTasks: "タスク：{count}",
  summaryWorkers: "稼働ワーカー：{count}",
  summaryPendingOldest: "最古 pending：{count} 秒",
  summaryLeaseRisk: "lease リスク：{count}",
  summaryControlPlaneState: "制御面状態：{value}",
  summaryCriticalAlerts: "重大警告：{count}",
  summaryWarningAlerts: "警告件数：{count}",
  summaryWarningActions: "警告アクション：{count}",
  summarySaturatedActions: "逼迫アクション：{count}",
  summaryCapabilityBlockedActions: "能力不足アクション：{count}",
  summaryClaimConflicted: "Claim 競合：{count}",
  summaryRateLimited: "制限ブロック：{count}",
  summaryCapabilityMismatched: "能力不一致：{count}",
  summaryDispatchFailed: "配布失敗：{count}",
  summaryCoveredWorkerPolicies: "適用済み worker ポリシー：{count}",
  summaryMissingWorkerPolicies: "不足 worker ポリシー：{count}",
  summaryAgeWarningActions: "経年警告アクション：{count}",
  summaryRunning: "実行中：{count}",
  summaryFailed: "失敗：{count}",
  summaryRetryableFailed: "再試行可能失敗：{count}",
  summaryAutoRetryScheduled: "自動再試行待ち：{count}",
  legend: {
    provider: "実行提供元",
    images: "イメージ",
    instances: "実行インスタンス",
    docker: "コンテナインスタンス",
    tasks: "タスク",
    workers: "稼働ワーカー",
    pendingOldest: "最古 pending",
    leaseRisk: "lease リスク",
    controlPlaneState: "制御面状態",
    criticalAlerts: "重大警告",
    warningAlerts: "警告件数",
    warningActions: "警告アクション",
    saturatedActions: "逼迫アクション",
    capabilityBlockedActions: "能力不足アクション",
    claimConflicted: "Claim 競合",
    rateLimited: "制限ブロック",
    capabilityMismatched: "能力不一致",
    dispatchFailed: "配布失敗",
    coveredWorkerPolicies: "適用済み worker ポリシー",
    missingWorkerPolicies: "不足 worker ポリシー",
    ageWarningActions: "経年警告アクション",
    running: "実行中",
    failed: "失敗",
    retryableFailed: "再試行可能失敗",
    autoRetryScheduled: "自動再試行待ち",
  },
  providerLabel: "現在の実行提供元：{value}",
  centerBaseUrl: "実行基盤 URL：{value}",
  openCenter: "実行基盤を開く",
  openExamExecution: "試験実行ページを開く",
  cards: {
    access: "接続情報",
    controlPlane: "制御面概要",
  },
  ...platformAdminExecutionControlPlane,
  tabs: {
    images: "イメージ ({count})",
    instances: "実行インスタンス ({count})",
    docker: "コンテナインスタンス ({count})",
    tasks: "実行タスク ({count})",
    events: "ランタイムイベント",
    rawEvents: "生イベント",
    errorCodes: "エラーコード一覧 ({count})",
  },
  columns: {
    instance: "インスタンス ID",
    containerId: "コンテナ ID",
    businessApp: "サービスコード / ソースアプリ",
    businessObject: "業務オブジェクト",
    instanceKind: "インスタンス種別",
    runtimeType: "ランタイム種別",
    question: "問題",
    status: commonShortNouns.status,
    type: "種別",
    accessMethods: "アクセス方式",
    primaryAccessEndpoint: "プライマリアクセスエンドポイント",
    accessEndpoint: "アクセスエンドポイント",
    createdAt: commonShortFieldLabels.createdAt,
    expiresAt: "有効期限",
    destroyedAt: "破棄日時",
    image: "イメージ",
    hostPort: "ホストポート",
    containerPort: "コンテナポート",
    source: "ソース",
    size: "サイズ",
    defaultPort: "既定ポート",
    tags: "タグ",
    container: "コンテナ",
    updatedAt: commonShortFieldLabels.updatedAt,
    keyword: "キーワード",
    retryableOnly: "再試行可能のみ",
    task: "タスク",
    action: "操作種別",
    operator: "操作者",
    eventConclusion: "結論",
    requestId: "冪等キー",
    trace: "トレース",
    retryFrom: "再試行元",
    retryState: "再試行状態",
    failureCode: "失敗コード",
    autoRetry: "自動再試行",
    error: "エラー",
    actions: "操作",
    resourceType: "リソース種別",
    eventStatus: "イベント状態",
    eventCategory: "イベント種別",
    event: "イベント",
    resource: "リソース",
    eventTime: "発生時刻",
    code: "エラーコード",
    name: commonShortFieldLabels.name,
    category: "分類",
    stage: "段階",
    retryable: "再試行可",
    description: "説明",
  },
  values: {
    enabled: "有効",
    disabled: "無効",
    undefinedRuntimeTargetStatus: "未定義実行ターゲット状態",
    yes: "はい",
    no: "いいえ",
    ...platformAdminExecutionRuntimeValues.values,
    retryable: platformAdminExecutionEvents.values.retryable,
    retried: platformAdminExecutionEvents.values.retried,
    succeeded: platformAdminExecutionEvents.values.succeeded,
    exhausted: platformAdminExecutionEvents.values.exhausted,
    scheduled: platformAdminExecutionEvents.values.scheduled,
    retryScheduled: platformAdminExecutionEvents.values.retryScheduled,
    processing: platformAdminExecutionEvents.values.processing,
    dispatchRetried: platformAdminExecutionEvents.values.dispatchRetried,
    dispatchFailed: platformAdminExecutionEvents.values.dispatchFailed,
    deadLettered: platformAdminExecutionEvents.values.deadLettered,
    manualInterventionRequired: platformAdminExecutionEvents.values.manualInterventionRequired,
    autoRetryExhausted: platformAdminExecutionEvents.values.autoRetryExhausted,
    pending: platformAdminExecutionEvents.values.pending,
    running: platformAdminExecutionEvents.values.running,
    success: platformAdminExecutionEvents.values.success,
    failed: platformAdminExecutionEvents.values.failed,
    cancelled: platformAdminExecutionEvents.values.cancelled,
    eventTaskPending: platformAdminExecutionEvents.values.eventTaskPending,
    eventTaskClaimed: platformAdminExecutionEvents.values.eventTaskClaimed,
    eventTaskClaimReleased: platformAdminExecutionEvents.values.eventTaskClaimReleased,
    eventTaskRunning: platformAdminExecutionEvents.values.eventTaskRunning,
    eventTaskSucceeded: platformAdminExecutionEvents.values.eventTaskSucceeded,
    eventTaskFailed: platformAdminExecutionEvents.values.eventTaskFailed,
    eventTaskCancelled: platformAdminExecutionEvents.values.eventTaskCancelled,
    eventTaskCancellationRequested:
      platformAdminExecutionEvents.values.eventTaskCancellationRequested,
    eventTaskLeaseRenewed: platformAdminExecutionEvents.values.eventTaskLeaseRenewed,
    eventTaskClaimConflicted: platformAdminExecutionEvents.values.eventTaskClaimConflicted,
    eventTaskDispatchCapabilityMismatch:
      platformAdminExecutionEvents.values.eventTaskDispatchCapabilityMismatch,
    eventTaskDispatchRateLimited: platformAdminExecutionEvents.values.eventTaskDispatchRateLimited,
    eventTaskAutoRetryDispatchFailed:
      platformAdminExecutionEvents.values.eventTaskAutoRetryDispatchFailed,
    eventTaskAutoRetryDeadLettered:
      platformAdminExecutionEvents.values.eventTaskAutoRetryDeadLettered,
    eventTaskAutoRetryManualInterventionRequired:
      platformAdminExecutionEvents.values.eventTaskAutoRetryManualInterventionRequired,
    eventTaskStaleRunningDetected:
      platformAdminExecutionEvents.values.eventTaskStaleRunningDetected,
    eventStatusRecorded: platformAdminExecutionEvents.values.eventStatusRecorded,
    eventCategoryTask: platformAdminExecutionEvents.values.eventCategoryTask,
    eventCategoryDispatch: platformAdminExecutionEvents.values.eventCategoryDispatch,
    eventCategoryLease: platformAdminExecutionEvents.values.eventCategoryLease,
    eventCategoryRecovery: platformAdminExecutionEvents.values.eventCategoryRecovery,
    eventCategoryOther: platformAdminExecutionEvents.values.eventCategoryOther,
    resourceExecutionTask: platformAdminExecutionEvents.values.resourceExecutionTask,
    resourceEnvironmentInstance: platformAdminExecutionEvents.values.resourceEnvironmentInstance,
    resourceExecutionTaskWithId: platformAdminExecutionEvents.values.resourceExecutionTaskWithId,
    resourceEnvironmentInstanceWithId:
      platformAdminExecutionEvents.values.resourceEnvironmentInstanceWithId,
    eventConclusionSuccess: platformAdminExecutionEvents.values.eventConclusionSuccess,
    eventConclusionFailed: platformAdminExecutionEvents.values.eventConclusionFailed,
    eventConclusionFailedWithReason:
      platformAdminExecutionEvents.values.eventConclusionFailedWithReason,
    eventConclusionRetryState: platformAdminExecutionEvents.values.eventConclusionRetryState,
  },
  legendSemantic: {
    all: "すべて",
    total: "合計",
    online: "オンライン",
    offline: "オフライン",
    unknown: "不明",
    enabled: "有効",
    disabled: "無効",
    running: "実行中",
    failed: "失敗",
    destroyed: "破棄済み",
    warning: "警告",
    draft: "下書き",
    healthy: "正常",
    default: "既定",
    active: "有効中",
    current: "現在",
    retry: "再試行",
    remaining: "残り",
    pending: "処理中",
    succeeded: "成功",
    published: "公開済み",
    archived: "アーカイブ済み",
    public: "公開",
    private: "非公開",
    shared: "共有",
    mine: "自分",
    used: "使用済み",
    unused: "未使用",
    visible: "表示",
    hidden: "非表示",
    inactive: "未有効",
    degraded: "劣化",
    mvp: "基本",
    reserved: "予約",
    container: "コンテナ",
    vm: "VM",
    expired: "期限切れ",
    console: "コンソール",
    recycles: "回収タスク",
    operator: "操作者",
    create: "作成",
    update: "更新",
    delete: "削除",
    template: "テンプレート",
  },
  actions: {
    view: "表示",
    retry: platformAdminExecutionEvents.actions.retry,
    retryTask: platformAdminExecutionEvents.actions.retryTask,
    processAutoRetries: platformAdminExecutionEvents.actions.processAutoRetries,
  },
  drawer: {
    title: platformAdminExecutionEvents.drawer.title,
    titleShort: platformAdminExecutionEvents.drawer.titleShort,
    eventTitle: platformAdminExecutionEvents.drawer.eventTitle,
    eventTitleShort: platformAdminExecutionEvents.drawer.eventTitleShort,
    instanceTitle: "インスタンス #{id}",
    dockerInstanceTitle: "ランタイムコンテナ · {id}",
    rawEventTitle: platformAdminExecutionEvents.drawer.rawEventTitle,
    rawEventTitleShort: platformAdminExecutionEvents.drawer.rawEventTitleShort,
    requestPayload: platformAdminExecutionEvents.drawer.requestPayload,
    resultPayload: platformAdminExecutionEvents.drawer.resultPayload,
  },
  fields: {
    appName: "業務表示名",
    appKey: "サービスコード",
    businessObject: "業務オブジェクト",
    instanceKind: "インスタンス種別",
    runtimeType: "ランタイム種別",
    providerType: "ランタイム提供元",
    providerKey: "提供元識別子",
    nodeKey: "実行ノード",
    targetKey: "実行ターゲット",
    providerRef: "ランタイム参照",
    taskId: platformAdminExecutionEvents.fields.taskId,
    tenantId: "テナント ID",
    organizationId: "組織 ID",
    examId: "試験 ID",
    questionId: "問題 ID",
    attemptId: "受験 ID",
    attemptQuestionId: "設問解答 ID",
    candidateId: "受験者 ID",
    sourceType: "ソース種別",
    objectType: "オブジェクト種別",
    questionTitle: "問題タイトル",
    displayOrder: "表示順",
    questionType: "業務問題種別",
    environmentType: "環境種別",
    eventAction: platformAdminExecutionEvents.fields.eventAction,
    eventTrigger: platformAdminExecutionEvents.fields.eventTrigger,
    workerId: platformAdminExecutionEvents.fields.workerId,
    requiredCapability: platformAdminExecutionEvents.fields.requiredCapability,
    dispatchDecision: platformAdminExecutionEvents.fields.dispatchDecision,
    leaseOwner: platformAdminExecutionEvents.fields.leaseOwner,
    leaseExpiresAt: platformAdminExecutionEvents.fields.leaseExpiresAt,
    retrySource: platformAdminExecutionEvents.fields.retrySource,
    rootTask: platformAdminExecutionEvents.fields.rootTask,
    retryDepth: platformAdminExecutionEvents.fields.retryDepth,
    retryCount: platformAdminExecutionEvents.fields.retryCount,
    autoRetryEnabled: platformAdminExecutionEvents.fields.autoRetryEnabled,
    autoRetryState: platformAdminExecutionEvents.fields.autoRetryState,
    autoRetryMax: platformAdminExecutionEvents.fields.autoRetryMax,
    autoRetryDelay: platformAdminExecutionEvents.fields.autoRetryDelay,
    nextRetryAt: platformAdminExecutionEvents.fields.nextRetryAt,
    latestRetry: platformAdminExecutionEvents.fields.latestRetry,
    startTime: platformAdminExecutionEvents.fields.startTime,
    finishTime: platformAdminExecutionEvents.fields.finishTime,
  },
  contract: {
    missingServiceIdentity: "source_app/app_key が不足しています",
  },
  columnHelp: {
    event: platformAdminExecutionEvents.columnHelp.event,
    resource: platformAdminExecutionEvents.columnHelp.resource,
    status: platformAdminExecutionEvents.columnHelp.status,
    eventConclusion: platformAdminExecutionEvents.columnHelp.eventConclusion,
    eventCategory: platformAdminExecutionEvents.columnHelp.eventCategory,
  },
  messages: {
    taskDetailLoadFailed: platformAdminExecutionEvents.messages.taskDetailLoadFailed,
    taskRetryStarted: platformAdminExecutionEvents.messages.taskRetryStarted,
    taskRetryFailed: platformAdminExecutionEvents.messages.taskRetryFailed,
    autoRetryProcessed: platformAdminExecutionEvents.messages.autoRetryProcessed,
    autoRetryProcessFailed: platformAdminExecutionEvents.messages.autoRetryProcessFailed,
    errorCodesEmpty:
      "現在エラーコード一覧が空です。通常は execution-fabric の読取失敗、直読状態の異常、またはページデータ未読込を意味します。",
  },
  units: {
    seconds: "{value} 秒",
    workersShort: "ワーカー",
  },
} as const;
